import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  ButtonGroup,
} from '@mui/material';
import {
  Refresh,
  ContentCopy,
  Download,
  OpenInNew,
} from '@mui/icons-material';

const DEFAULT_CAPABILITIES = {
  download: true,
  open: true,
  convert: true,
  copy: true,
};

const LaTeXRenderer = ({
  latexCode,
  loading,
  remoteRenderUrl,
  onPdfReady,
  enablePdf = true,
  rawOnlyMessage,
  capabilities = DEFAULT_CAPABILITIES,
}) => {
  const [renderError, setRenderError] = useState(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [isFetchingPdf, setIsFetchingPdf] = useState(false);
  const [errorDetails, setErrorDetails] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const objectUrlRef = useRef(null);

  const cleanedLatex = useMemo(() => {
    if (!latexCode) return '';
    return latexCode
      .replace(/^```latex\s*/, '')
      .replace(/```\s*$/, '')
      .trim();
  }, [latexCode]);

  useEffect(() => {
    let didCancel = false;

    if (!enablePdf) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPdfUrl('');
      setRenderError(false);
      setFallbackUsed(false);
      setErrorDetails('');
      setIsFetchingPdf(false);
      if (typeof onPdfReady === 'function') {
        onPdfReady('');
      }
      return () => {};
    }

    if (!cleanedLatex || loading) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPdfUrl('');
      setRenderError(false);
      setFallbackUsed(false);
      setErrorDetails('');
      if (typeof onPdfReady === 'function') {
        onPdfReady('');
      }
      return;
    }

    const controller = new AbortController();

    const fetchPdf = async () => {
      setIsFetchingPdf(true);
      setRenderError(false);
      setFallbackUsed(false);
      setErrorDetails('');

      try {
        const fetchWithPost = async () => {
          const response = await fetch('/api/proxy-latex', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ latex: cleanedLatex }),
            signal: controller.signal,
          });
          if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            const combined =
              errorText || `PDF generation failed with status ${response.status}`;
            const needsFallback =
              response.status === 404 ||
              response.status === 405 ||
              /Cannot POST/i.test(errorText);
            const error = new Error(combined);
            error.shouldFallback = needsFallback;
            throw error;
          }
          return response;
        };

        const fetchWithGet = async () => {
          const response = await fetch(
            `/api/proxy-latex?text=${encodeURIComponent(cleanedLatex)}`,
            { method: 'GET', signal: controller.signal }
          );
          if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            throw new Error(
              errorText || `GET fallback failed with status ${response.status}`
            );
          }
          return response;
        };

        let response;
        try {
          response = await fetchWithPost();
        } catch (postError) {
          if (postError.shouldFallback) {
            response = await fetchWithGet();
          } else {
            throw postError;
          }
        }

        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        if (!contentType.includes('application/pdf')) {
          const errorText = await response.text().catch(() => '');
          const err = new Error(
            errorText || `Unexpected response (content-type: ${contentType || 'unknown'})`
          );
          err.shouldUseRemote = Boolean(remoteRenderUrl);
          throw err;
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = objectUrl;

        setPdfUrl(objectUrl);
        if (typeof onPdfReady === 'function') {
          onPdfReady(objectUrl);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Error processing LaTeX:', err);
        if (remoteRenderUrl) {
          console.warn('Falling back to remote LaTeX rendering service.');
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
          }
          setPdfUrl('');
          setRenderError(false);
          setFallbackUsed(false);
          setErrorDetails('');
          if (typeof onPdfReady === 'function') {
            onPdfReady(remoteRenderUrl);
          }
        } else {
          setRenderError(true);
          setErrorDetails(err.message || 'Unexpected error while compiling LaTeX.');
          setPdfUrl('');
          if (typeof onPdfReady === 'function') {
            onPdfReady('');
          }
        }
      } finally {
        if (!didCancel) {
          setIsFetchingPdf(false);
        }
      }
    };

    fetchPdf();

    return () => {
      didCancel = true;
      controller.abort();
    };
  }, [
    cleanedLatex,
    loading,
    retryNonce,
    onPdfReady,
    enablePdf,
    remoteRenderUrl,
  ]);

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    },
    []
  );

  const copyToClipboard = () => {
    if (!cleanedLatex || !capabilities.copy) return;

    navigator.clipboard
      .writeText(cleanedLatex)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleRetry = () => {
    if (!cleanedLatex) return;
    setFallbackUsed(false);
    setRenderError(false);
    setRetryNonce(prev => prev + 1);
  };

  const handleDownloadPdf = () => {
    if (!pdfUrl || !capabilities.download) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'cv-preview.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleWordConversion = () => {
    if (!capabilities.convert) return;
    const source = pdfUrl || remoteRenderUrl;
    if (!source) return;
    window.open(
      `https://www.pdf2go.com/pdf-to-word?url=${encodeURIComponent(source)}`,
      '_blank',
      'noopener'
    );
  };

  const handleOpenPdf = () => {
    if (!capabilities.open) return;
    const source = pdfUrl || remoteRenderUrl;
    if (!source) {
      handleRetry();
      return;
    }
    window.open(source, '_blank', 'noopener');
  };

  const renderRawLatexView = (options = {}) => {
    const {
      title = 'Raw LaTeX View',
      description = 'PDF rendering is temporarily unavailable. Review or edit the LaTeX below, then try compiling again.',
      primaryActionLabel = 'Try PDF View',
      onPrimaryAction = enablePdf ? handleRetry : null,
      showRetryButton = enablePdf,
    } = options;

    if (!cleanedLatex) {
      return (
        <Box
          sx={{ textAlign: 'center', p: 4, border: '1px solid #f0f0f0', borderRadius: 1 }}
        >
          <Typography variant="h6" color="text.secondary">
            No LaTeX content available.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 4, height: '700px', overflowY: 'auto' }}>
        <Box
          sx={{
            p: 2,
            bgcolor: '#f0f7ff',
            borderLeft: '4px solid #0066ff',
            mb: 2,
            borderRadius: '4px',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {showRetryButton && (
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={onPrimaryAction || handleRetry}
              >
                {primaryActionLabel}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={copyToClipboard}
              disabled={!capabilities.copy}
            >
              Copy LaTeX
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            p: 3,
            bgcolor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontFamily: '"Courier New", monospace',
            fontSize: '14px',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
          }}
        >
          {cleanedLatex}
        </Box>

        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" variant="filled">
            LaTeX code copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    );
  };

  if (!enablePdf) {
    return renderRawLatexView({
      title: 'Limited LaTeX Preview',
      description:
        rawOnlyMessage ||
        'Upgrade to unlock the full PDF preview. Below is a partial LaTeX snapshot.',
      showRetryButton: false,
    });
  }

  if (fallbackUsed && cleanedLatex) {
    return renderRawLatexView();
  }

  if (renderError) {
    return (
      <Box
        sx={{ textAlign: 'center', p: 4, border: '1px solid #f0f0f0', borderRadius: 1 }}
      >
        <Typography variant="h6" color="error">
          Error rendering LaTeX
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
          There was a problem rendering your CV. This could be due to LaTeX syntax issues or
          compiler errors.
        </Typography>
        {errorDetails && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, whiteSpace: 'pre-wrap' }}
          >
            {errorDetails}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRetry}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={() => {
              setFallbackUsed(true);
              setRenderError(false);
            }}
            disabled={!capabilities.copy}
          >
            View Raw LaTeX
          </Button>
        </Box>

        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" variant="filled">
            LaTeX code copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  const showLoader = isFetchingPdf && !pdfUrl && !remoteRenderUrl;
  const effectivePdfUrl = pdfUrl || remoteRenderUrl || '';

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '800px',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography sx={{ textAlign: 'center' }}>
          {showLoader
            ? 'Compiling your LaTeX into a PDF...'
            : effectivePdfUrl
              ? 'PDF preview ready. Use the actions below if needed.'
              : 'Unable to compile automatically. Try re-running the compile or view the raw LaTeX.'}
        </Typography>

        <ButtonGroup
          variant="contained"
          sx={{
            alignSelf: 'center',
            flexWrap: 'wrap',
            '& > *': { mb: 1 },
          }}
        >
          <Button
            startIcon={<Download />}
            onClick={handleDownloadPdf}
            disabled={!pdfUrl || !capabilities.download}
          >
            Download PDF
          </Button>
          <Button
            startIcon={<OpenInNew />}
            onClick={handleOpenPdf}
            disabled={!effectivePdfUrl || !capabilities.open}
          >
            Open PDF
          </Button>
          <Button
            startIcon={<Download />}
            onClick={handleWordConversion}
            disabled={!effectivePdfUrl || !capabilities.convert}
          >
            Convert to Word
          </Button>
          <Button
            startIcon={<ContentCopy />}
            onClick={copyToClipboard}
            disabled={!capabilities.copy}
          >
            Copy LaTeX
          </Button>
        </ButtonGroup>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFallbackUsed(true)}
          >
            View Raw LaTeX
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleRetry}
            disabled={showLoader}
            startIcon={<Refresh />}
          >
            Re-run Compile
          </Button>
        </Box>

        {remoteRenderUrl && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            PDF fallback opens in a new tab. Word conversion uses an external service.
          </Typography>
        )}
      </Box>

      <Box sx={{ position: 'relative', flexGrow: 1 }}>
        {showLoader && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.85)',
              zIndex: 5,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Generating PDF...
            </Typography>
          </Box>
        )}

        {effectivePdfUrl ? (
          <iframe
            name="pdf-frame"
            title="CV Preview"
            width="100%"
            height="100%"
            src={effectivePdfUrl}
            style={{ border: 'none' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#fafafa',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No PDF available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try re-running the compile or view the raw LaTeX to troubleshoot.
            </Typography>
          </Box>
        )}
      </Box>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          LaTeX code copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LaTeXRenderer;
